import { X } from "lucide-react";
import { useEffect, useId } from "react";

// Track ids of currently-open modals. Body scroll is locked while any modal is open and
// released only when the set becomes empty. Keying by a stable per-instance id makes the
// registration idempotent, so nested/stacked or simultaneously-closing modals (e.g. the
// gladiator detail + force-retire dialog) can never leave the page scroll-locked ("frozen").
const openModals = new Set();

function syncBodyLock() {
  document.body.style.overflow = openModals.size > 0 ? "hidden" : "";
}

export default function Modal({ open, onClose, title, children, wide }) {
  const modalId = useId();
  useEffect(() => {
    if (!open) return;
    openModals.add(modalId);
    syncBodyLock();
    return () => {
      openModals.delete(modalId);
      syncBodyLock();
    };
  }, [open, modalId]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4" data-testid="modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose || undefined} />
      <div
        className={`relative bg-zinc-950 border border-amber-700/40 shadow-[0_0_40px_rgba(0,0,0,0.8)] w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[calc(100dvh-0.75rem)] overflow-y-auto rounded-t-sm fade-up sm:max-h-[88vh] sm:rounded-none`}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-amber-900/30 bg-zinc-950 px-5 py-3 sm:py-4">
          <h3 className="font-cinzel text-lg text-amber-300 uppercase tracking-wide">{title}</h3>
          {onClose && <button type="button" data-testid="modal-close" onClick={onClose} aria-label="Close dialog" title="Close dialog" className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center text-zinc-400 transition-colors hover:text-amber-300"><X size={22} /></button>}
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
